import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface ModalContextType {
  modalStack: string[];
  registerModal: (id: string) => void;
  unregisterModal: (id: string) => void;
  bringToTop: (id: string) => void;
  getZIndex: (id: string) => number;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

const BASE_Z_INDEX = 50;
const Z_INDEX_INCREMENT = 10;

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalStack, setModalStack] = useState<string[]>([]);

  const registerModal = useCallback((id: string) => {
    setModalStack((prev) => {
      // If modal already exists, don't add it again
      if (prev.includes(id)) {
        return prev;
      }
      // Add new modal to the top of the stack
      return [...prev, id];
    });
  }, []);

  const unregisterModal = useCallback((id: string) => {
    setModalStack((prev) => prev.filter((modalId) => modalId !== id));
  }, []);

  const bringToTop = useCallback((id: string) => {
    setModalStack((prev) => {
      // Remove the modal from its current position
      const filtered = prev.filter((modalId) => modalId !== id);
      // Add it to the top
      return [...filtered, id];
    });
  }, []);

  const getZIndex = useCallback(
    (id: string) => {
      const index = modalStack.indexOf(id);
      if (index === -1) return BASE_Z_INDEX;
      // Higher index in stack = higher z-index
      return BASE_Z_INDEX + (index + 1) * Z_INDEX_INCREMENT;
    },
    [modalStack]
  );

  return (
    <ModalContext.Provider
      value={{
        modalStack,
        registerModal,
        unregisterModal,
        bringToTop,
        getZIndex,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error("useModalContext must be used within a ModalProvider");
  }
  return context;
};
